import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  ShieldAlert,
  TrendingDown,
  FileWarning,
  DollarSign,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { financialAPI } from '../services/api';

const AnomalyDetectionPanel = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    loadAnomalies();
  }, []);

  const loadAnomalies = async () => {
    setLoading(true);
    try {
      const response = await financialAPI.getAnomalyDetection();
      setAnomalies(response.data.anomalies || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to load anomalies:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/10 border-red-500/50 text-red-600',
      high: 'bg-orange-500/10 border-orange-500/50 text-orange-600',
      medium: 'bg-yellow-500/10 border-yellow-500/50 text-yellow-600',
      low: 'bg-blue-500/10 border-blue-500/50 text-blue-600'
    };
    return colors[severity] || colors.medium;
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <ShieldAlert className="w-5 h-5" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'financial_leak':
        return <TrendingDown className="w-5 h-5 text-red-600" />;
      case 'data_anomaly':
        return <FileWarning className="w-5 h-5 text-orange-600" />;
      case 'inaccuracy':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'tax_error':
        return <DollarSign className="w-5 h-5 text-red-600" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      financial_leak: 'Kebocoran Keuangan',
      data_anomaly: 'Data Janggal',
      inaccuracy: 'Ketidakakuratan',
      tax_error: 'Error Perpajakan'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="glass-card p-6 rounded-xl border border-border/40">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          <span className="ml-3 text-muted-foreground">Scanning for anomalies...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header & Summary */}
      <div className="glass-card p-6 rounded-xl border border-border/40">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-3 rounded-lg ${
              summary?.status === 'critical' ? 'bg-red-500/10' :
              summary?.status === 'warning' ? 'bg-yellow-500/10' :
              'bg-green-500/10'
            }`}>
              <ShieldAlert className={`w-6 h-6 ${
                summary?.status === 'critical' ? 'text-red-600' :
                summary?.status === 'warning' ? 'text-yellow-600' :
                'text-green-600'
              }`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">🤖 AI Anomaly Detection</h2>
              <p className="text-sm text-muted-foreground">
                Last scan: {summary?.last_scan ? new Date(summary.last_scan).toLocaleString('id-ID') : '-'}
              </p>
            </div>
          </div>
          <button
            onClick={loadAnomalies}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Scan Ulang</span>
          </button>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="p-4 bg-red-500/5 border border-red-500/30 rounded-lg">
              <p className="text-xs text-red-600 mb-1">Critical</p>
              <p className="text-2xl font-bold text-red-600">{summary.severity_breakdown.critical}</p>
            </div>
            <div className="p-4 bg-orange-500/5 border border-orange-500/30 rounded-lg">
              <p className="text-xs text-orange-600 mb-1">High</p>
              <p className="text-2xl font-bold text-orange-600">{summary.severity_breakdown.high}</p>
            </div>
            <div className="p-4 bg-yellow-500/5 border border-yellow-500/30 rounded-lg">
              <p className="text-xs text-yellow-600 mb-1">Medium</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.severity_breakdown.medium}</p>
            </div>
            <div className="p-4 bg-blue-500/5 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-600 mb-1">Low</p>
              <p className="text-2xl font-bold text-blue-600">{summary.severity_breakdown.low}</p>
            </div>
          </div>
        )}

        {summary?.total_financial_impact > 0 && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-600 font-medium">
              ⚠️ Total Financial Impact: <span className="text-lg font-bold">Rp {(summary.total_financial_impact / 1000000).toFixed(1)}M</span>
            </p>
          </div>
        )}
      </div>

      {/* Status Message */}
      {anomalies.length === 0 ? (
        <div className="glass-card p-8 rounded-xl border border-green-500/30 bg-green-500/5 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 rounded-full mb-4">
            <ShieldAlert className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-green-600 mb-2">✅ Tidak Ada Anomali Terdeteksi</h3>
          <p className="text-sm text-muted-foreground">
            Sistem keuangan Anda dalam kondisi baik. Semua data terlihat normal dan akurat.
          </p>
        </div>
      ) : (
        /* Anomalies List */
        <div className="space-y-3">
          {anomalies.map((anomaly, index) => (
            <div
              key={index}
              className={`glass-card rounded-xl border-2 overflow-hidden transition-all ${getSeverityColor(anomaly.severity)}`}
            >
              {/* Header */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      {getSeverityIcon(anomaly.severity)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        {getTypeIcon(anomaly.type)}
                        <span className="text-xs font-semibold px-2 py-0.5 bg-background/50 rounded">
                          {getTypeLabel(anomaly.type)}
                        </span>
                        <span className="text-xs font-bold uppercase px-2 py-0.5 bg-background/50 rounded">
                          {anomaly.severity}
                        </span>
                      </div>
                      <h3 className="font-bold text-foreground mb-1">{anomaly.title}</h3>
                      <p className="text-sm text-foreground/80">{anomaly.description}</p>
                    </div>
                  </div>
                  <button className="flex-shrink-0 ml-3">
                    {expandedIndex === index ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedIndex === index && (
                <div className="px-4 pb-4 space-y-3 border-t border-current/20">
                  {/* Affected Items */}
                  {anomaly.affected_items && anomaly.affected_items.length > 0 && (
                    <div className="pt-3">
                      <p className="text-xs font-semibold mb-2">Affected Items:</p>
                      <div className="bg-background/30 rounded-lg p-3">
                        <ul className="text-xs space-y-1">
                          {anomaly.affected_items.map((item, i) => (
                            <li key={i} className="font-mono">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Financial Impact */}
                  {anomaly.amount_impact && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs font-semibold text-red-600">
                        Financial Impact: <span className="text-lg">Rp {anomaly.amount_impact.toLocaleString('id-ID')}</span>
                      </p>
                    </div>
                  )}

                  {/* Recommendation */}
                  <div className="p-3 bg-primary/5 border border-primary/30 rounded-lg">
                    <p className="text-xs font-semibold mb-1 text-primary">💡 Recommended Action:</p>
                    <p className="text-xs text-foreground">{anomaly.recommendation}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnomalyDetectionPanel;